import {
    Checkbox,
    Label, PhoneInput, RadioList, Select, SelectOption, TextInput, useValidatedState
} from "acma-spa";
import React, { useEffect } from "react";
import * as yup from 'yup';
import { ISPAMComplaint, ProgressStageProps, SPAMComplaint, SPAMComplaintContactState, SPAMComplaintContactTitle } from "../models/Models";
import { NextPreviousButtons } from "./next-previous-buttons";
import { isValidPhoneNumber } from 'react-phone-number-input'

export interface PersonalDetailsProps extends ProgressStageProps {}
export const postcodeRegExp = /^(0[289][0-9]{2})|([1-9][0-9]{3})$/

const validatePhone = async (phone: string | undefined) => {
    if (typeof (phone) === "undefined")
        return true;
    return isValidPhoneNumber(phone);
}

export interface IValidatedContact extends Omit<ISPAMComplaint, 'messages'> {

}

const schema: yup.ObjectSchema<IValidatedContact> = yup.object().shape({
    anonymous: yup.bool().required().label("This"),
    governmentReferred: yup.bool().required().label("This"),
    noUnsubscribe: yup.bool().when('anonymous', {
        is: true,
        then: (schema) => schema.required().isTrue("This is a required field").label("This"),
        otherwise: (schema) => schema,
    }),
    businessReferred: yup.bool().when('anonymous', {
        is: false,
        then: (schema) => schema.required().label("This"),
        otherwise: (schema) => schema,
    }),
    serviceProviderReferred: yup.bool().when('anonymous', {
        is: false,
        then: (schema) => schema.required().label("This"),
        otherwise: (schema) => schema,
    }),
    writtenStatement: yup.bool().when('anonymous', {
        is: false,
        then: (schema) => schema.required().label("This"),
        otherwise: (schema) => schema,
    }),
    firstName: yup.string().when('anonymous', {
        is: false,
        then: (schema) => schema.required().label("This").max(50),
        otherwise: (schema) => schema,
    }),
    lastName: yup.string().when('anonymous', {
        is: false,
        then: (schema) => schema.required().label("This").max(50),
        otherwise: (schema) => schema,
    }),
    contactNumber: yup.string().when('anonymous', {
        is: false,
        then: (schema) => schema.label("This").test('', 'Please enter a valid phone number', validatePhone).max(50),
        otherwise: (schema) => schema,
    }),
    postcode: yup.string().when('anonymous', {
        is: false,
        then: (schema) => schema.matches(postcodeRegExp, "Postcode is not valid").label("This").max(4),
        otherwise: (schema) => schema,
    }),
    email: yup.string().when('anonymous', {
        is: false,
        then: (schema) => schema.email().required().label("This").max(100),
        otherwise: (schema) => schema,
    }),
    title: yup.mixed<SPAMComplaintContactTitle>().optional(),
    state: yup.mixed<SPAMComplaintContactState>().optional()
}).defined();

const titleOptions = () => {
    let options: SelectOption<SPAMComplaintContactTitle>[] = [];
    options.push({ label: '', value: undefined })
    for (const key in SPAMComplaintContactTitle) {
        if (typeof SPAMComplaintContactTitle[key] === "string") {
            options.push({
                label: SPAMComplaintContactTitle[key],
                value: SPAMComplaintContactTitle[key],
            });
        }
    }
    return options;
};

const stateOptions = () => {
    let options: SelectOption<SPAMComplaintContactState>[] = [];
    options.push({ label: '', value: undefined })
    for (const key in SPAMComplaintContactState) {
        if (typeof SPAMComplaintContactState[key] === "string") {
            options.push({
                label: SPAMComplaintContactState[key],
                value: SPAMComplaintContactState[key],
            });
        }
    }
    return options;
};

export const PersonalDetails = (props: PersonalDetailsProps) => {

    const [submission, setSubmission, errors, validate] = useValidatedState<ISPAMComplaint>(props.submission, schema);
    useEffect(() => {
        if (props.onChange && submission !== props.submission) {
            props.onChange(submission);
        }
    }, [submission])

    const handleNext = async () => {
        const result = await validate("#personal-details-container");
        console.log(result)
        console.log(errors)
        if (result && props.onNext) {
            props.onNext(submission);
        }
    };

    return (<>
        <div id="personal-details-container">
        <div className="row">
            <div className="col col-12 mb-4">
                <h3>About you</h3>
                <hr />
            </div>
        </div>
        <div className="row">
                <div className="col col-12 mb-4">
                    <Label
                        id="anonymous"
                        error={errors.anonymous}
                        label=""
                    >
                    <RadioList
                        id="anonymous"
                        value={submission.anonymous}
                        options={[
                            {
                                label: 'I would like to provide my contact details',
                                value: false
                            },
                            {
                                label: 'I would like to remain anonymous',
                                value: true
                            }
                        ]}
                        onChange={(v) => setSubmission({ anonymous: v })}
                            size="block" />
                        </Label>
            </div>
            </div>
            {submission.anonymous === false && <><div>
                <div className="row">
                    <div className="col col-12 mt-4 mb-4">
                        <Label
                            id="title"
                            label="Title"
                        >
                            <Select<SPAMComplaintContactTitle>
                                id="title"
                                value={submission.title}
                                onChange={(v) => setSubmission({ title: v })}
                                options={titleOptions()}
                                size="block"
                            />
                        </Label>
                    </div>
                </div>
                <div className="row">
                    <div className="col col-12 mb-4">
                        <Label
                            id="firstName"
                            label="First Name"
                            error={errors.firstName}
                            required
                        >
                            <TextInput
                                id="firstName"
                                value={submission.firstName}
                                onChange={(v) => setSubmission({ firstName: v })}
                                size="block" />
                        </Label>
                    </div>
                </div>
                <div className="row">
                    <div className="col col-12 mb-4">
                        <Label
                            id="lastName"
                            label="Last Name"
                            error={errors.lastName}
                            required
                        >
                            <TextInput
                                id="lastName"
                                value={submission.lastName}
                                onChange={(v) => setSubmission({ lastName: v })}
                                size="block" />
                        </Label>
                    </div>
                </div>
                <div className="row">
                    <div className="col col-12 mb-4">
                        <Label
                            id="contactNumber"
                            label="Contact number"
                            error={errors.contactNumber}
                        >
                            <PhoneInput
                                id="contactNumber"
                                value={submission.contactNumber}
                                onChange={(v) => setSubmission({ contactNumber: v })} />
                        </Label>
                    </div>
                </div>
                <div className="row">
                    <div className="col col-12 mb-4">
                        <Label
                            id="postcode"
                            label="Postcode"
                            error={errors.postcode}
                        >
                            <TextInput
                                id="postcode"
                                value={submission.postcode}
                                onChange={(v) => setSubmission({ postcode: v })}
                                maxLength={4}
                                size="block" />
                        </Label>
                    </div>
                </div>
                <div className="row">
                    <div className="col col-12 mb-4">
                        <Label
                            id="state"
                            label="State"
                        >
                            <Select<SPAMComplaintContactState>
                                id="state"
                                value={submission.state}
                                onChange={(v) => setSubmission({ state: v })}
                                options={stateOptions()}
                                size="block"
                            />
                        </Label>
                    </div>
                </div>
                <div className="row">
                    <div className="col col-12 mb-4">
                        <Label
                            id="email"
                            label="Email"
                            error={errors.email}
                            required
                        >
                            <TextInput
                                id="email"
                                value={submission.email}
                                onChange={(v) => setSubmission({ email: v })}
                                size="block" />
                        </Label>
                    </div>
                </div>
            </div>       
            <div className="row">
                <div className="col col-12">
                    <h3>How my information will be used</h3>
                    <hr />
                </div>
            </div>
             <div className="row">
                <div className="col col-12 mb-4">
                    <Label
                            id="governmentReferred"
                            error={errors.governmentReferred}
                        label="I agree that the ACMA may refer details of my complaint to other government agencies, including overseas, as part of any investigation into breaches of the Spam Act or other laws. "
                        className="mt-4"
                        required
                    >
                        <RadioList
                                id="governmentReferred"
                                value={submission.governmentReferred}
                            options={[
                                {
                                    label: 'Yes',
                                    value: true
                                },
                                {
                                    label: 'No',
                                    value: false
                                }
                            ]}
                            onChange={(v) => {
                                setSubmission({
                                    governmentReferred: v
                                })
                            }}
                            />
                    </Label>
                </div>
            </div>
            <div className="row">
                <div className="col col-12 mb-4">
                    <Label
                        id="businessReferred"
                        label="Where we can, we may ask the business to stop contacting you.  I agree that the ACMA may pass on my email or phone number to the business or organisation I am complaining about. "
                            className="mt-4"
                            error={errors.businessReferred}
                        required
                    >
                        <RadioList
                                id="businessReferred"
                                value={submission.businessReferred}
                            options={[
                                {
                                    label: 'Yes',
                                    value: true
                                },
                                {
                                    label: 'No',
                                    value: false
                                }
                            ]}
                            onChange={(v) => {
                                setSubmission({
                                    businessReferred: v
                                })
                            }}
                        />
                    </Label>
                </div>
            </div>
            <div className="row">
                <div className="col col-12 mb-4">
                    <Label
                        id="serviceProviderReferred"
                        label="I agree that the ACMA provide my email address or phone number to a service provider (such as a Phone service), as part of compliance or enforcement actions. "
                            className="mt-4"
                            error={errors.serviceProviderReferred}
                        required
                    >
                        <RadioList
                                id="serviceProviderReferred"
                                value={submission.serviceProviderReferred}
                            options={[
                                {
                                    label: 'Yes',
                                    value: true
                                },
                                {
                                    label: 'No',
                                    value: false
                                }
                            ]}
                            onChange={(v) => {
                                setSubmission({
                                    serviceProviderReferred: v
                                })
                            }}
                        />
                    </Label>
                </div>
            </div>
            <div className="row">
                <div className="col col-12 mb-4">
                    <Label
                        id="writtenStatement"
                        label="If necessary, I am prepared to make a written statement about my complaint."
                            className="mt-4"
                            error={errors.writtenStatement}
                        required
                    >
                        <RadioList
                                id="writtenStatement"
                                value={submission.writtenStatement}
                            options={[
                                {
                                    label: 'Yes',
                                    value: true
                                },
                                {
                                    label: 'No',
                                    value: false
                                }
                            ]}
                            onChange={(v) => {
                                setSubmission({
                                    writtenStatement: v
                                })
                            }}
                        />
                    </Label>
                </div>
            </div>
            </>
            }
            {submission.anonymous && <>
            <div className="row">
                <div className="col col-12 mb-4">
                    <Label
                        id="noUnsubscribe"
                        label="I understand this means that the ACMA will not be able to request that I be unsubscribed from any mailing list and that I will not be contacted again in relation to this complaint."
                            className="mt-4"
                            error={errors.noUnsubscribe}
                        required
                    >
                        <Checkbox
                            id="noUnsubscribe"
                                label="I understand"
                                checked={submission.noUnsubscribe}
                                onChange={(v) => {
                                setSubmission({
                                    noUnsubscribe: v
                                })
                            }}
                        />
                    </Label>
                </div>
            </div>
            <div className="row">
                <div className="col col-12 mt-2">
                    <h3>How my information will be used</h3>
                    <hr />
                </div>
            </div>
               <div className="row">
                <div className="col col-12 mb-4">
                    <Label
                            id="governmentReferred"
                            error={errors.governmentReferred}
                        label="I agree that the ACMA may refer details of my complaint to other government agencies, including overseas, as part of any investigation into breaches of the Spam Act or other laws. "
                        className="mt-4"
                        required
                    >
                        <RadioList
                                id="governmentReferred"
                                value={submission.governmentReferred}
                            options={[
                                {
                                    label: 'Yes',
                                    value: true
                                },
                                {
                                    label: 'No',
                                    value: false
                                }
                            ]}
                            onChange={(v) => {
                                setSubmission({
                                    governmentReferred: v
                                })
                            }}
                        />
                    </Label>
                </div>
            </div>
        </>
            }
        </div>
        <NextPreviousButtons
            onPrevious={props.onPrevious}
            onNext={handleNext}
        />
    </>
    );
}